import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import { format, parseISO, isValid } from "date-fns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import ConfirmDialog from "components/common/ConfirmDialog";
import { useFacility } from "hooks/useFacility";
import {
  createAvailability,
  deleteAvailability,
  subscribeFacilityAvailability,
} from "services/careverify/careVerifyBookingService";

function formatRange(row) {
  const toDate = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
      const d = parseISO(v);
      return isValid(d) ? d : null;
    }
    if (v?.toDate) return v.toDate();
    return null;
  };
  const start = toDate(row.start);
  const end = toDate(row.end);
  if (!start) return String(row.start || "");
  const startLabel = format(start, "MMM d, yyyy h:mm a");
  const endLabel = end ? format(end, "h:mm a") : "";
  return endLabel ? `${startLabel} – ${endLabel}` : startLabel;
}

/**
 * Facility Ops — CareVerify Tier 2 availability CRUD.
 * Does not modify the wait-time Schedule page.
 * Accepts `data` from DashboardRoutes; falls back to useFacility for AdminRoutes.
 */
const FacilityAvailabilityPage = ({ data: dataProp }) => {
  const { data: facilityData } = useFacility();
  const data = dataProp?.id ? dataProp : facilityData;
  const facilityId = data?.id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createdOk, setCreatedOk] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      visitType: "elective",
      capacityModel: "discrete_slot",
      start: "",
      end: "",
      capacityRemaining: 1,
    },
  });

  const visitType = watch("visitType");

  useEffect(() => {
    // Keep capacityModel paired with visitType (§5).
    setValue(
      "capacityModel",
      visitType === "urgent" ? "capacity_window" : "discrete_slot",
    );
  }, [visitType, setValue]);

  useEffect(() => {
    if (!facilityId) return undefined;
    setLoading(true);
    const unsub = subscribeFacilityAvailability(
      facilityId,
      (list) => {
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error("[FacilityAvailabilityPage]", err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [facilityId]);

  const onCreate = async (values) => {
    setCreating(true);
    setCreateError(null);
    setCreatedOk(null);
    try {
      const capacityRemaining = Number(values.capacityRemaining);
      await createAvailability({
        facilityId,
        visitType: values.visitType,
        capacityModel: values.capacityModel,
        start: new Date(values.start).toISOString(),
        end: new Date(values.end).toISOString(),
        capacityRemaining,
        allowedVisitTypes: [values.visitType],
      });
      setCreatedOk("Availability saved.");
      reset({
        visitType: values.visitType,
        capacityModel: values.capacityModel,
        start: "",
        end: "",
        capacityRemaining: values.visitType === "urgent" ? 12 : 1,
      });
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAvailability({
        facilityId,
        slotOrWindowId: deleteTarget.id,
      });
      setDeleteTarget(null);
    } catch (err) {
      console.error("[FacilityAvailabilityPage] delete failed:", err);
    } finally {
      setDeleting(false);
    }
  };

  if (!facilityId) {
    return (
      <div className="inner">
        <DashboardPageHeader
          title="CareVerify availability"
          subtitle="Manage appointment slots and urgent capacity windows."
        />
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="inner">
      <DashboardPageHeader
        title="CareVerify availability"
        subtitle="Tier 2 self-managed slots (elective) and capacity windows (urgent). Separate from wait-time Schedule."
      />

      {createdOk ? (
        <Alert
          severity="success"
          onClose={() => setCreatedOk(null)}
          sx={{ mb: 2 }}
        >
          {createdOk}
        </Alert>
      ) : null}
      {createError ? (
        <Alert
          severity="error"
          onClose={() => setCreateError(null)}
          sx={{ mb: 2 }}
        >
          {createError}
        </Alert>
      ) : null}

      <Box
        component="form"
        onSubmit={handleSubmit(onCreate)}
        sx={{ mb: 3, maxWidth: 560 }}
      >
        <Typography variant="h6" gutterBottom>
          Add availability
        </Typography>
        <Stack spacing={2}>
          <Controller
            name="visitType"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField {...field} select label="Visit type">
                <MenuItem value="elective">Elective (discrete slot)</MenuItem>
                <MenuItem value="urgent">Urgent (capacity window)</MenuItem>
              </TextField>
            )}
          />
          <Controller
            name="capacityModel"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Capacity model"
                InputProps={{ readOnly: true }}
                helperText="Paired automatically with visit type."
              />
            )}
          />
          <Controller
            name="start"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Start"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <Controller
            name="end"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="End"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <Controller
            name="capacityRemaining"
            control={control}
            rules={{
              required: true,
              min: 0,
              validate: (v) =>
                Number.isInteger(Number(v)) || "Must be a whole number",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Capacity remaining"
                type="number"
                inputProps={{ min: 0, step: 1 }}
              />
            )}
          />
          <LoadingButton
            type="submit"
            variant="contained"
            loading={creating}
            disabled={!isValid}
          >
            Add
          </LoadingButton>
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6" gutterBottom>
        Current availability
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No availability yet.</Typography>
      ) : (
        <List>
          {rows.map((row) => (
            <ListItem
              key={row.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="Delete availability"
                  onClick={() => setDeleteTarget(row)}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              }
            >
              <ListItemText
                primary={formatRange(row)}
                secondary={`${row.visitType} · ${row.capacityModel} · ${row.capacityRemaining ?? "—"} remaining`}
              />
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => (!deleting ? setDeleteTarget(null) : undefined)}
        title="Delete availability?"
        message="This removes the slot or window from CareVerify. Existing bookings are not deleted."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default FacilityAvailabilityPage;
